import { useEffect, useRef } from "react";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, IconButton, useMediaQuery, useTheme } from "@mui/material";

export type PageHeaderHighlight = boolean | string | React.CSSProperties;

export interface PageHeaderProps {
  onBack?: () => void;
  left?: React.ReactNode;
  middle?: React.ReactNode;
  right?: React.ReactNode;
  /**
   * When true, the rendered header height is written to the global CSS variable
   * `--top-bar-height` so downstream sticky layout can offset against it.
   * Default false — opt in per call site, because multiple PageHeader instances
   * on the same page would otherwise race each other to set the global variable.
   */
  measureHeight?: boolean;
  /**
   * Tints the header background. `true` uses a default subtle grey; a string
   * sets `backgroundColor`; an object spreads onto the root style.
   */
  highlighted?: PageHeaderHighlight;
  backArrowAriaLabel?: string;
  /**
   * When true, the left/middle/right slots share equal width on large screens so
   * the middle sits at the true page centre regardless of how wide the side slots
   * are. Default false keeps the middle filling the slack between the sides.
   */
  centerMiddle?: boolean;
}

const TOP_BAR_HEIGHT_CSS_VAR = "--top-bar-height";

const resolveHighlight = (
  highlighted: PageHeaderHighlight | undefined,
  tint: string,
): React.CSSProperties => {
  // The header is sticky, so it has to stay opaque. `tint` is a translucent
  // palette value (action.hover), so it is layered as a background-image over
  // the root's opaque background-color rather than replacing it.
  if (highlighted === true)
    return { backgroundImage: `linear-gradient(${tint}, ${tint})` };
  if (typeof highlighted === "string") return { backgroundColor: highlighted };
  if (highlighted && typeof highlighted === "object") return highlighted;
  return {};
};

export const PageHeader: React.FC<PageHeaderProps> = ({
  onBack,
  left,
  middle,
  right,
  measureHeight = false,
  highlighted,
  backArrowAriaLabel = "back",
  centerMiddle = false,
}) => {
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.up("md"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.up("sm"));
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("lg"));
  const containerRef = useRef<HTMLDivElement>(null);

  // The bar is painted with background.paper, which MUI sets equal to
  // background.default — the colour MainLayout paints the page with — in *both*
  // palettes. So the surface alone never separates the bar from the page. In
  // light mode the black boxShadow below does that job; against dark's #121212
  // a black shadow is invisible, so lift the surface with the standard
  // action.hover overlay instead (#121212 -> ~#252525). Light mode is untouched.
  const needsSurfaceLift = theme.palette.mode === "dark" && !highlighted;

  useEffect(() => {
    if (!measureHeight) return;

    const updateHeight = () => {
      if (containerRef.current) {
        document.documentElement.style.setProperty(
          TOP_BAR_HEIGHT_CSS_VAR,
          `${containerRef.current.offsetHeight}px`,
        );
      }
    };

    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    window.addEventListener("resize", updateHeight);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateHeight);
      document.documentElement.style.removeProperty(TOP_BAR_HEIGHT_CSS_VAR);
    };
  }, [measureHeight]);

  const hasLeft = Boolean(onBack || left);
  const hasMiddle = Boolean(middle);
  const hasRight = Boolean(right);

  return (
    <div
      ref={containerRef}
      style={{
        position: "sticky",
        top: 0,
        zIndex: 150,
        backgroundColor: theme.palette.background.paper,
        borderTop: `1px solid ${theme.palette.divider}`,
        // Edge against the page content below. borderTop above faces the app
        // AppBar instead, so without this the bar had no lower boundary at all.
        borderBottom: `1px solid ${theme.palette.divider}`,
        boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.15)",
        padding: isSmallScreen ? "8px 16px" : "6px 12px",
        ...(needsSurfaceLift
          ? {
              backgroundImage: `linear-gradient(${theme.palette.action.hover}, ${theme.palette.action.hover})`,
            }
          : {}),
        ...resolveHighlight(highlighted, theme.palette.action.hover),
      }}
    >
      <Box
        sx={{
          /**
           * On large screens `centerMiddle` lays the bar out as a grid with equal,
           * shrinkable side columns (`minmax(0, 1fr)`) around a natural-width middle
           * column, so the middle sits at the true page centre no matter how wide
           * the side slots are. Otherwise it's the default flex row.
           */
          display: centerMiddle && isLargeScreen ? "grid" : "flex",
          gridTemplateColumns:
            centerMiddle && isLargeScreen
              ? "minmax(0, 1fr) auto minmax(0, 1fr)"
              : undefined,
          alignItems: "center",
          flexWrap: isLargeScreen ? "nowrap" : "wrap",
          justifyContent: isLargeScreen ? "flex-start" : "center",
          gap: isLargeScreen ? "12px" : isMediumScreen ? "8px" : "6px",
          width: "100%",
        }}
      >
        {hasLeft && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              justifySelf: "start",
              flexShrink: isLargeScreen ? 0 : 1,
              minWidth: 0,
            }}
          >
            {onBack && (
              <IconButton
                onClick={onBack}
                aria-label={backArrowAriaLabel}
                color="primary"
              >
                <ArrowBackIcon />
              </IconButton>
            )}
            {left}
          </Box>
        )}
        {hasMiddle && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              justifySelf: "center",
              /**
               * On large screens, fill the middle; on small screens, take the
               * whole row so it centres cleanly when the bar wraps. (Under the
               * `centerMiddle` grid the middle is a natural-width auto column.)
               */
              flex: isLargeScreen ? 1 : "0 0 100%",
              minWidth: 0,
            }}
          >
            {middle}
          </Box>
        )}
        {hasRight && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 0.5,
              justifySelf: "end",
              flexShrink: isLargeScreen ? 0 : 1,
              minWidth: 0,
              justifyContent:
                centerMiddle && isLargeScreen ? "flex-end" : "center",
              /**
               * Push the right slot to the far edge in the flex layout so the back
               * arrow stays left and the actions stay right. (No effect in grid.)
               */
              marginLeft: "auto",
            }}
          >
            {right}
          </Box>
        )}
      </Box>
    </div>
  );
};
